/**
 * iter1041 — BHI Metabolic Delta Card.
 *
 * "Твоят гърден метаболизъм преди 30 дни vs днес." Reads from
 * `/api/breast-intelligence/metabolic-delta?window_days=30` and renders
 * per-component deltas (insulin, inflammation, estrogen, adiposity) as
 * horizontal bars — up-arrow green for improvement, down-arrow red for
 * regression.
 *
 * When history is insufficient we render an "encouragement empty state"
 * rather than a fake chart, so patients understand what to do next.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Minus, Loader2, ChartBar } from "lucide-react";
import { useReadingLevel } from "@/contexts/BhiReadingLevelContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Higher = better direction for all metabolic sub-scores (they're 0–100).
const COMPONENT_META = {
  total:        { label_bg: "Общ метаболитен резултат", plain_bg: "Общ резултат" },
  insulin:      { label_bg: "Инсулинова чувствителност", plain_bg: "Реакция към сладко" },
  inflammation: { label_bg: "Възпалителен товар",         plain_bg: "Спокойствие в клетките" },
  estrogen:     { label_bg: "Естрогенна сигнализация",    plain_bg: "Баланс на хормоните" },
  adiposity:    { label_bg: "Мастна тъкан (композиция)",  plain_bg: "Съотношение мазнина/мускул" },
};

export default function BhiMetabolicDeltaCard({ isDark = false }) {
  const [state, setState] = useState({ loading: true, data: null });
  const { isPlain } = useReadingLevel();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/breast-intelligence/metabolic-delta?window_days=30`)
      .then(({ data }) => { if (alive) setState({ loading: false, data }); })
      .catch(() => { if (alive) setState({ loading: false, data: null }); });
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <div className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}
           data-testid="bhi-metabolic-delta-card">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Зареждам история…
        </div>
      </div>
    );
  }

  const d = state.data;
  if (!d) return null;

  if (d.state === "insufficient_history") {
    return (
      <div
        data-testid="bhi-metabolic-delta-empty"
        className={`rounded-2xl border p-4 ${
          isDark ? "bg-slate-900 border-slate-800 text-slate-300"
                 : "bg-slate-50 border-slate-200 text-slate-700"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            isDark ? "bg-sky-500/10 text-sky-300" : "bg-sky-50 text-sky-600"
          }`}>
            <ChartBar className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">
              Промяна — преди {d.window_days} дни спрямо сега
            </div>
            <p className="text-xs opacity-75 mt-1 break-words">
              {d.hint_bg}
            </p>
            <div className="mt-2 text-[11px] opacity-60">
              Запазени снимки на състоянието досега: <strong>{d.snapshots_count}</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render deltas
  const rows = ["total", "insulin", "inflammation", "estrogen", "adiposity"]
    .map((k) => ({ key: k, meta: COMPONENT_META[k], delta: d.deltas?.[k], cur: d.current?.[k], base: d.baseline?.[k] }))
    .filter((r) => r.delta !== null && r.delta !== undefined);

  return (
    <div
      data-testid="bhi-metabolic-delta-card"
      className={`rounded-2xl border overflow-hidden ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}
    >
      <div className={`px-4 py-3 border-b ${isDark ? "border-slate-800" : "border-slate-100"} flex items-start gap-3`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-600"
        }`}>
          <ChartBar className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">
            Твоят гърден метаболизъм — {d.window_days} дни vs сега
          </div>
          <div className="text-[11px] opacity-70 mt-0.5">
            Сравнение: {formatDate(d.baseline?.created_at)} → {formatDate(d.current?.created_at)}
          </div>
        </div>
      </div>
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {rows.map((r) => (
          <DeltaRow key={r.key} row={r} isPlain={isPlain} isDark={isDark} />
        ))}
      </ul>
    </div>
  );
}

function DeltaRow({ row, isPlain, isDark }) {
  const { meta, delta, cur, base } = row;
  const label = isPlain && meta.plain_bg ? meta.plain_bg : meta.label_bg;
  const arrow = delta > 0.5 ? TrendingUp : delta < -0.5 ? TrendingDown : Minus;
  const Arrow = arrow;
  const tone = delta > 0.5
    ? (isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-50")
    : delta < -0.5
      ? (isDark ? "text-rose-300 bg-rose-500/10" : "text-rose-700 bg-rose-50")
      : (isDark ? "text-slate-400 bg-slate-800" : "text-slate-500 bg-slate-100");

  // Bar visualization — width proportional to |delta| up to 20 pts.
  const barW = Math.min(100, Math.abs(delta) * 5);

  return (
    <li className="px-4 py-3" data-testid={`bhi-delta-row-${row.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold min-w-0 flex-1 truncate">{label}</div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${tone}`}>
          <Arrow className="w-3 h-3" />
          {delta > 0 ? "+" : ""}{delta.toFixed(1)}
        </div>
      </div>
      <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
        <div
          className={`h-full rounded-full ${
            delta > 0.5 ? "bg-emerald-500" : delta < -0.5 ? "bg-rose-500" : "bg-slate-400"
          }`}
          style={{ width: `${barW}%` }}
        />
      </div>
      <div className="text-[10px] opacity-60 mt-1">
        Преди: <strong>{Number(base ?? 0).toFixed(0)}</strong> · Сега: <strong>{Number(cur ?? 0).toFixed(0)}</strong>
      </div>
    </li>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("bg-BG", { day: "2-digit", month: "short" });
  } catch { return iso; }
}
